// ------------------------------------------------------------------------------
// name: useLoginNext
// author: mudas
// created: 2025/4/1
// ------------------------------------------------------------------------------

import { useNavigate } from 'react-router';
import { at } from 'lodash-es';
import { getEnv, isAbsoluteURL } from 'common/utils';

import { ROUTER_PATHNAME } from 'common/router/config';
import { To } from '@remix-run/router';
import service from 'common/service';

enum UserActionType {
  UserAgreementRead = 'userAgreementRead',
  PhoneBind = 'phoneBind',
  RoleSelect = 'roleSelect',
  NicknameBind = 'nicknameBind',
  SystemUpdate = 'systemUpdate',
}

const UserActionRouter = {
  [UserActionType.UserAgreementRead]: `/${ROUTER_PATHNAME.PROTOCOL_UPDATE}`,
  [UserActionType.PhoneBind]: `/${ROUTER_PATHNAME.LOGIN}/${ROUTER_PATHNAME.BIND_PHONE}`,
  [UserActionType.RoleSelect]: `/${ROUTER_PATHNAME.LOGIN}/${ROUTER_PATHNAME.CHOOSE_ROLE}`,
  [UserActionType.NicknameBind]: `/${ROUTER_PATHNAME.NICKNAME_SETTING}`,
  [UserActionType.SystemUpdate]: `/${ROUTER_PATHNAME.PLATFORM_UPDATE}`,
};

// 业务按顺序要求进行，不可调整和错乱
const ActionPriority = [
  UserActionType.RoleSelect,
  UserActionType.UserAgreementRead,
  UserActionType.PhoneBind,
  UserActionType.NicknameBind,
  UserActionType.SystemUpdate,
];

export const encodeNextPath = (path: string) => `base64:${btoa(encodeURIComponent(path))}`;
export const decodeNextPath = (path: string) => decodeURIComponent(atob(path.replace('base64:', '')));

const normalizeSearch = (search?: string | URLSearchParams) => {
  if (search instanceof URLSearchParams) {
    return search;
  } else if (typeof search === 'string') {
    return new URLSearchParams(search);
  }

  return new URLSearchParams(location.search);
};

// 获取url中的next跳转参数
export const getNextPath = (search?: string | URLSearchParams) => {
  const params = normalizeSearch(search);
  const next = params.get('next') || '';
  return next?.startsWith('base64:') ? decodeNextPath(next) : decodeURIComponent(next);
};

// 是否在天工平台内进行业务操作和跳转（外部网站共享登录需要附加 out 参数，值无所谓）
// 若 next 指向外部绝对网址，也当作外部平台处理
export const isOutPlatform = (search?: string | URLSearchParams) => {
  const params = normalizeSearch(search);
  return params.has('out') || isAbsoluteURL(getNextPath(search));
};

export const useLoginNext = () => {
  const nav = useNavigate();

  const getUserStatus = async () => {
    const isInternal = getEnv().isInternal;

    if (isInternal) return '';

    try {
      const { data } = await service.userSetting.postLoginStatus();

      if (data) {
        // 根据data里的字段，根据优先级，找到第一个为false的字段，跳转到对应的页面
        const vals = at(data, ActionPriority);
        const index = vals.findIndex((item) => !item);

        return UserActionRouter[ActionPriority[index]] ?? '';
      } else {
        return '';
      }
    } catch (e) {
      return '';
    }
  };

  const toNext = async () => {
    // 需要判断是新用户
    const isInternal = getEnv().isInternal;
    const actionNext = await getUserStatus();

    // 登录流程后续的补充流程页面，不能再次通过next参数进行跳转，需要在使用 safeToLogin 时附加 ignoreNext=false 来忽略
    // 如在绑定手机界面，点击关闭按钮回到登录界面
    if (actionNext) {
      nav(actionNext);
    } else {
      // 应忽略登录流程后续的补充流程页面，不能通过 next 参数进行跳转，而是通过 actionNext 跳转
      const path = getNextPath() || '/task';

      if (isInternal) {
        window.location.href = path;
      } else {
        nav(path);
      }
    }
  };

  /**
   * 跳转到目标页面
   * - 与 toNext() 不同的是，toNext() 会注重用户当前资料的相关状态，直接跳转到目标页指导完成相关必要资料的录入
   * - 该方法直接跳转到目标页，若未指定将从 url 提取 next 属性目标页，若 url 也没有参数，则跳转到 root 主页
   * @param path 跳转到指定页面，若未指定
   */
  const onlyNext = async (path?: To) => {
    nav(path || getNextPath() || '/');
  };

  const agreement = async () => {
    // 同意签署登录协议
    await service.userSetting.userAgreement();
  };

  return { getUserStatus, toNext, onlyNext, agreement };
};
